import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getDocumentLang } from "../../utils/helpers";
import { getAiFeatureFlags } from "../../utils/aiFeatureFlags";
import { useLessonTranscript } from "../../hooks/useLessonTranscript";
import SubtitlesControls from "./SubtitlesControls";
import QuickActionsPanel from "./QuickActionsPanel";
import LessonChatPanel from "./LessonChatPanel";
import AiQuizPanel from "./AiQuizPanel";

const BASE_TABS = ["assist", "chat"];

const AiLessonPanel = ({
  lessonId,
  getPlayedSeconds,
  onSubtitleSegmentsChange,
}) => {
  const { t } = useTranslation("ai");
  const currentUser = useSelector((state) => state.auth.currentUser);
  const { lessonTranscriptVisible } = getAiFeatureFlags(
    currentUser?.organization,
  );

  const tabs = lessonTranscriptVisible ? [...BASE_TABS, "quiz"] : BASE_TABS;
  const defaultTab = "assist";

  const [tab, setTab] = useState(defaultTab);
  const [subtitleEnabled, setSubtitleEnabled] = useState(true);
  const [subtitleLanguage, setSubtitleLanguage] = useState(
    () => getDocumentLang() || "en",
  );
  const [segments, setSegments] = useState(null);

  const {
    status: transcriptStatus,
    isReady: transcriptReady,
    isGenerating: transcriptGenerating,
    isPolling: transcriptPolling,
    timedOut: transcriptTimedOut,
    generate: generateTranscript,
  } = useLessonTranscript(lessonId, { enabled: lessonTranscriptVisible });

  useEffect(() => {
    setTab(defaultTab);
    setSegments(null);
    setSubtitleEnabled(true);
    setSubtitleLanguage(getDocumentLang() || "en");
  }, [lessonId, defaultTab]);

  useEffect(() => {
    if (!lessonTranscriptVisible && tab === "quiz") {
      setTab("assist");
    }
  }, [lessonTranscriptVisible, tab]);

  useEffect(() => {
    if (lessonTranscriptVisible && subtitleEnabled && segments?.length) {
      onSubtitleSegmentsChange?.(segments);
    } else {
      onSubtitleSegmentsChange?.(null);
    }
  }, [
    lessonTranscriptVisible,
    subtitleEnabled,
    segments,
    onSubtitleSegmentsChange,
  ]);

  const handleSegmentsChange = useCallback((next) => {
    setSegments(next);
  }, []);

  return (
    <div className="ai-lesson-panel">
      {lessonTranscriptVisible && (
        <SubtitlesControls
          lessonId={lessonId}
          transcriptReady={transcriptReady}
          transcriptStatus={transcriptStatus}
          transcriptGenerating={transcriptGenerating}
          transcriptPolling={transcriptPolling}
          transcriptTimedOut={transcriptTimedOut}
          onGenerateTranscript={generateTranscript}
          subtitleEnabled={subtitleEnabled}
          onSubtitleEnabledChange={setSubtitleEnabled}
          subtitleLanguage={subtitleLanguage}
          onSubtitleLanguageChange={setSubtitleLanguage}
          onSegmentsChange={handleSegmentsChange}
        />
      )}

      <ul className="nav nav-tabs ai-lesson-tabs" role="tablist">
        {tabs.map((key) => (
          <li className="nav-item" key={key} role="presentation">
            <button
              type="button"
              className={`nav-link ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
            >
              {t(`tabs.${key}`)}
            </button>
          </li>
        ))}
      </ul>

      <div className="ai-lesson-panel__body">
        {tab === "assist" && <QuickActionsPanel lessonId={lessonId} />}
        {tab === "chat" && (
          <LessonChatPanel
            lessonId={lessonId}
            getPlayedSeconds={getPlayedSeconds}
          />
        )}
        {lessonTranscriptVisible && tab === "quiz" && (
          <AiQuizPanel lessonId={lessonId} transcriptReady={transcriptReady} />
        )}
      </div>
    </div>
  );
};

export default AiLessonPanel;
